import { createRouter, createWebHistory } from 'vue-router'

// Import views
import Login from '../views/Login.vue'
import Home from '../views/Home.vue'
import ClientList from '../views/clients/ClientList.vue'
import ClientDetail from '../views/clients/ClientDetail.vue'
import ClientCreate from '../views/clients/ClientCreate.vue'
import ClientEdit from '../views/clients/ClientEdit.vue'
import ScriptList from '../views/scripts/ScriptList.vue'
import ScriptCreate from '../views/scripts/ScriptCreate.vue'
import ScriptEdit from '../views/scripts/ScriptEdit.vue'
import ScriptLogsView from '../views/scripts/ScriptLogsView.vue'
import RoleList from '../views/roles/RoleList.vue'
import RoleCreate from '../views/roles/RoleCreate.vue'
import RoleEdit from '../views/roles/RoleEdit.vue'
import UserList from '../views/users/UserList.vue'
import UserCreate from '../views/users/UserCreate.vue'
import UserEdit from '../views/users/UserEdit.vue'
import MonitoringDashboard from '../views/monitoring/MonitoringDashboard.vue'
import LogsView from '../views/logs/LogsView.vue'
import NotFound from '../views/NotFound.vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      title: 'Iniciar Sesión',
      description: 'Acceso al sistema MyGeotab Feeder',
      public: true,  // Esta ruta no requiere autenticación
      layout: 'BlankLayout'  // Usar layout sin encabezado
    }
  },
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      title: 'Dashboard',
      description: 'Panel principal del sistema MyGeotab Feeder'
    }
  },
  {
    path: '/clients',
    name: 'ClientList',
    component: ClientList,
    meta: {
      title: 'Clientes',
      description: 'Gestión de clientes'
    }
  },
  {
    path: '/clients/create',
    name: 'ClientCreate',
    component: ClientCreate,
    meta: {
      title: 'Nuevo Cliente',
      description: 'Crear nuevo cliente'
    }
  },
  {
    path: '/clients/:id',
    name: 'ClientDetail',
    component: ClientDetail,
    meta: {
      title: 'Detalle del Cliente',
      description: 'Ver información detallada del cliente'
    },
    props: true
  },
  {
    path: '/clients/:id/edit',
    name: 'ClientEdit',
    component: ClientEdit,
    meta: {
      title: 'Editar Cliente',
      description: 'Editar información del cliente'
    },
    props: true
  },
  {
    path: '/scripts',
    name: 'ScriptList',
    component: ScriptList,
    meta: {
      title: 'Scripts',
      description: 'Gestión de scripts de ejecución'
    }
  },
  {
    path: '/scripts/create',
    name: 'ScriptCreate',
    component: ScriptCreate,
    meta: {
      title: 'Nuevo Script',
      description: 'Crear nueva ejecución de script'
    }
  },
  {
    path: '/scripts/:id/edit',
    name: 'ScriptEdit',
    component: ScriptEdit,
    meta: {
      title: 'Editar Script',
      description: 'Editar configuración del script'
    },
    props: true
  },
  {
    path: '/scripts/:id/logs',
    name: 'ScriptLogsView',
    component: ScriptLogsView,
    meta: {
      title: 'Logs del Script',
      description: 'Visualización de logs del script'
    },
    props: true
  },
  {
    path: '/roles',
    name: 'RoleList',
    component: RoleList,
    meta: {
      title: 'Roles',
      description: 'Gestión de roles y permisos'
    }
  },
  {
    path: '/roles/create',
    name: 'RoleCreate',
    component: RoleCreate,
    meta: {
      title: 'Nuevo Rol',
      description: 'Crear nuevo rol'
    }
  },
  {
    path: '/roles/:id/edit',
    name: 'RoleEdit',
    component: RoleEdit,
    meta: {
      title: 'Editar Rol',
      description: 'Editar información del rol'
    },
    props: true
  },
  {
    path: '/users',
    name: 'UserList',
    component: UserList,
    meta: {
      title: 'Usuarios',
      description: 'Gestión de usuarios del sistema'
    }
  },
  {
    path: '/users/create',
    name: 'UserCreate',
    component: UserCreate,
    meta: {
      title: 'Nuevo Usuario',
      description: 'Crear nuevo usuario'
    }
  },
  {
    path: '/users/:id/edit',
    name: 'UserEdit',
    component: UserEdit,
    meta: {
      title: 'Editar Usuario',
      description: 'Editar información del usuario'
    },
    props: true
  },
  {
    path: '/monitoring',
    name: 'MonitoringDashboard',
    component: MonitoringDashboard,
    meta: {
      title: 'Monitoreo',
      description: 'Dashboard de monitoreo del sistema'
    }
  },
  {
    path: '/logs',
    name: 'LogsView',
    component: LogsView,
    meta: {
      title: 'Logs',
      description: 'Visualización de logs del sistema'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: {
      title: 'Página no encontrada',
      description: 'La página solicitada no existe'
    }
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Update document title
  const baseTitle = 'MyGeotab Feeder'
  document.title = to.meta.title ? `${to.meta.title} - ${baseTitle}` : baseTitle

  // Update meta description
  if (to.meta.description) {
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', to.meta.description)
    }
  }

  // Import auth store dynamically to avoid circular dependencies
  const { useAuthStore } = await import('../stores/auth')
  const authStore = useAuthStore()

  // Public routes that don't require authentication
  const publicRoutes = ['Login']
  const isPublicRoute = publicRoutes.includes(to.name) || to.meta.public

  // Check authentication
  if (!isPublicRoute && !authStore.isAuthenticated) {
    // Not authenticated and trying to access protected route
    return next({ name: 'Login' })
  }

  // If authenticated, check client-specific access control
  if (authStore.isAuthenticated && authStore.user?.client_id) {
    // User is associated with a client - restrict access
    const allowedRoutesForClient = [
      'ClientDetail',
      'Login'
    ]

    // If trying to access a route that is not allowed for client users
    if (!allowedRoutesForClient.includes(to.name)) {
      // Redirect to their client detail page
      if (to.name !== 'ClientDetail') {
        return next({
          name: 'ClientDetail',
          params: { id: authStore.user.client_id }
        })
      }
    }

    // If accessing ClientDetail but not their own client, redirect to their client
    if (to.name === 'ClientDetail' && to.params.id != authStore.user.client_id) {
      return next({
        name: 'ClientDetail',
        params: { id: authStore.user.client_id }
      })
    }
  }

  next()
})

export default router